import { Link, useLocation } from 'react-router-dom'
import { Sparkles, ClipboardCheck, Stethoscope, CalendarDays, ArrowRight, MessageCircle } from 'lucide-react'
import { useChatWidgetStore } from '@/stores/chatWidgetStore'

const TOOLS = [
  {
    href: '/screening',
    title: 'Free Child Screening',
    desc: 'Quick 5-minute questionnaire with instant red-flag report.',
    icon: ClipboardCheck,
    color: 'from-emerald-500 to-teal-600',
    chat: false,
  },
  {
    href: '/triage',
    title: 'Symptom Triage (AI)',
    desc: "Describe your child's symptoms — get recommended next steps.",
    icon: Stethoscope,
    color: 'from-sky-500 to-blue-600',
    chat: false,
  },
  {
    href: '/activity-plan',
    title: 'Weekly Activity Plan',
    desc: 'AI-built home activities tailored to your child.',
    icon: CalendarDays,
    color: 'from-amber-500 to-orange-600',
    chat: false,
  },
  {
    href: null,
    title: 'Ask BRC Chatbot',
    desc: 'Instant answers on therapies, branches, fees — 24 × 7.',
    icon: MessageCircle,
    color: 'from-violet-500 to-purple-600',
    chat: true,
  },
]

/**
 * Prominent Free Tools strip shown above the footer on every page,
 * except on the tool pages themselves to avoid redundancy.
 */
export function FreeToolsStrip() {
  const { pathname } = useLocation()
  const openChat = useChatWidgetStore((s) => s.setOpen)

  // Hide on tool pages, admin, portal, auth, and booking
  const hidden =
    pathname.startsWith('/screening') ||
    pathname.startsWith('/triage') ||
    pathname.startsWith('/activity-plan') ||
    pathname.startsWith('/admin') ||
    pathname.startsWith('/portal') ||
    pathname.startsWith('/auth') ||
    pathname.startsWith('/book')

  if (hidden) return null

  return (
    <section className="relative bg-gradient-to-br from-indigo-50 via-white to-emerald-50 border-t border-border py-14 sm:py-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-3">
            <Sparkles className="w-3.5 h-3.5" /> 100% FREE · NO SIGN-UP
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">
            Free tools for every parent
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Get instant clarity on your child's development — no account, no payment, results in minutes.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {TOOLS.map((t) => {
            const Icon = t.icon
            const inner = (
              <>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${t.color} flex items-center justify-center mb-4 shadow-sm`}>
                  <Icon className="w-6 h-6 text-white" strokeWidth={2.2} />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-1.5">{t.title}</h3>
                <p className="text-sm text-muted-foreground flex-1">{t.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2.5 transition-all">
                  {t.chat ? 'Open chat' : 'Try now'} <ArrowRight className="w-4 h-4" />
                </span>
              </>
            )
            const cardCls =
              'group relative overflow-hidden rounded-2xl bg-white border border-border shadow-sm hover:shadow-lg transition-all duration-200 p-6 flex flex-col text-left w-full'
            return t.chat ? (
              <button key={t.title} onClick={() => openChat(true)} className={cardCls}>
                {inner}
              </button>
            ) : (
              <Link key={t.href!} to={t.href!} className={cardCls}>
                {inner}
              </Link>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default FreeToolsStrip
